import React, { lazy, Suspense } from 'react';
import { FieldValues, Path, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';

// Lazy-loaded so the TipTap editor bundle only ships to pages that render it.
const RichTextEditor = lazy(() => import('../RichTextEditor'));

interface RichTextEditorFieldProps<T extends FieldValues> {
  name: Path<T>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  /** Passed to the editor's link mark; see RichTextEditor. */
  linkClassName?: string;
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
}

export default function RichTextEditorField<T extends FieldValues>({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  error,
  linkClassName,
  register,
  setValue,
  watch
}: RichTextEditorFieldProps<T>) {
  const value = watch(name) || '';

  const handleChange = (content: string) => {
    setValue(name, content as T[Path<T>], { shouldValidate: true });
  };

  // Register the field with react-hook-form
  React.useEffect(() => {
    register(name, { required: required ? `${label || 'Field'} is required` : false });
  }, [register, name, required, label]);

  return (
    <Suspense
      fallback={
        <div className="space-y-2">
          {label && (
            <label className="block text-sm font-medium text-gray-700">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
          )}
          <div className="border rounded-lg bg-white p-4 min-h-[200px] text-sm text-gray-400">
            Loading editor…
          </div>
        </div>
      }
    >
      <RichTextEditor
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        label={label}
        required={required}
        disabled={disabled}
        error={error}
        linkClassName={linkClassName}
      />
    </Suspense>
  );
}
